import { Box, Drawer, Typography, IconButton } from '@mui/material';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import Grid from '@mui/material/Grid2';
import UploadFile from 'components/Common/UploadFile';
import { useEffect, useState } from 'react';
import AgentDataSetFormSkeleton from './AgentDataSetFormSkeleton';
import { useCreateOrUpdateAgentDataSet, useGetAgentDataSet } from 'apis/business/agent/hook';

export default function AgentDataSetForm() {
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const [selectedFiles, setSelectedFiles] = useState<null | any[]>(null);
  const { data: dataset, isLoading: isDataSetLoading } = useGetAgentDataSet();
  const { mutateAsync: handleDataSet, isPending } = useCreateOrUpdateAgentDataSet();

  const handleChange = async (files: File[]) => {
    const data = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      data.push(file);
    }
    setSelectedFiles(data);
  };

  const { handleSubmit, control, watch, reset } = useForm<any>({
    defaultValues: { datasource_type: 'document' },
    values: dataset ?? { name: '', datasource_type: 'document' }
    // resolver: zodResolver(DataSetSchema)
  });

  const active = watch('datasource_type');
  const nameValue = watch('name');

  useEffect(() => {
    if (!dataset) {
      reset({
        name: nameValue,
        datasource_type: active
      });
    }
  }, [reset, active, nameValue, dataset]);

  const onSubmit = (data: any) => {
    const formData = new FormData();
    prepareFormData(formData, data, 'dataset');
    if (selectedFiles) {
      selectedFiles.forEach((file) => {
        formData.append('documents[]', file);
      });
    }
    handleDataSet(formData);
  };

  const selectedDatasourceType = watch('datasource_type');

  const allowedFiles = allowedFilesMap[selectedDatasourceType] || [];

  if (isDataSetLoading) {
    return <AgentDataSetFormSkeleton />;
  }

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 600, md: 800 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: { xs: 2, lg: 3 },
          py: { xs: 1.5, lg: 2 },
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          Configuration
        </Typography>
        <IconButton
          onClick={() => navigate(-1)}
          size='small'
          sx={{
            p: { xs: 0.75, lg: 1 }
          }}
        >
          <IoClose size={20} />
        </IconButton>
      </Box>

      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto'
          }}
        >
          <Grid container padding={{ xs: 2, lg: 3 }} spacing={{ xs: 2, lg: 3 }} alignItems='center'>
            <Grid size={12}>
              <Input name='name' label='Name' control={control} fullWidth required sx={{ marginTop: 0.5 }} />
            </Grid>
            {!datasetId && (
              <>
                <Grid overflow='auto' size={12}>
                  {watch('datasource_type') !== 'website' && (
                    <UploadFile
                      selectedFiles={selectedFiles}
                      handleChange={handleChange}
                      allowedFileTypes={allowedFiles}
                      datasourceType={watch('datasource_type')}
                    />
                  )}
                  {watch('datasource_type') === 'website' && (
                    <Input name='configuration.url' label='Url' control={control} fullWidth />
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            px: { xs: 2, lg: 3 },
            py: { xs: 1.5, lg: 2 },
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: { xs: 'space-between', sm: 'flex-end' },
            gap: 2,
            flexShrink: 0,
            flexWrap: { xs: 'wrap', lg: 'nowrap' }
          }}
        >
          <Button
            color='secondary'
            variant='outlined'
            sx={{
              border: '1px solid #F3F3F3',
              width: { xs: '47%', sm: 'auto' }
            }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            isPending={isPending}
            sx={{
              width: { xs: '47%', sm: 'auto' }
            }}
          >
            {datasetId ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

export const allowedFilesMap: {
  [key: string]: string[];
} = {
  naive: ['docx', 'xlsx', 'pdf', 'txt', 'md', 'json', 'html'],
  document: ['docx', 'xlsx', 'pdf', 'txt', 'md', 'json', 'html'],
  qa: ['xlsx', 'csv'],
  resume: ['docx', 'pdf', 'txt'],
  manual: ['pdf'],
  _table: ['xlsx', 'csv', 'txt'],
  paper: ['pdf'],
  book: ['docx', 'pdf', 'txt'],
  laws: ['docx', 'pdf', 'txt'],
  presentation: ['pdf', 'pptx'],
  one: ['docx', 'xlsx', 'pdf', 'txt'],
  menu: ['xlsx', 'csv']
};

const prepareFormData = (formData: FormData, data: Record<string, any>, parentKey: string = '') => {
  for (const key in data) {
    // eslint-disable-next-line no-prototype-builtins
    if (data.hasOwnProperty(key)) {
      const value = data[key];
      const formKey = parentKey ? `${parentKey}[${key}]` : key;

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        prepareFormData(formData, value, formKey);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          prepareFormData(formData, { [`${key}[${index}]`]: item }, parentKey);
        });
      } else {
        formData.append(formKey, value);
      }
    }
  }
};
